import { ComponentProps, MouseEvent, useCallback, useMemo } from 'react'
import { cn } from '~utils/cn'

import Icon from '~components/ui/icon'
import TextInputLabel from '~components/ui/text-input/@base/label'
import { useSelectBox } from './context'

export type SelectBoxInputProps = Readonly<
  ComponentProps<'div'> & {
    placeholder?: string
    value?: string | null
    disabled?: boolean
    color?: 'white' | 'background'
    validateErrorMessage?: (value?: string | null) => string
  }
>

const SelectBoxInput = ({
  placeholder = 'Select...',
  value,
  disabled,
  color = 'white',
  onClick,
  className,
  validateErrorMessage,
  ...props
}: SelectBoxInputProps) => {
  const errorMessage = useMemo(() => {
    if (!validateErrorMessage) return ''
    if (value === undefined) return ''
    return validateErrorMessage(value)
  }, [validateErrorMessage, value])
  const { selectedData, handleDropdown, open, label } = useSelectBox()

  const valueText = value !== undefined ? value : selectedData?.label

  const onSelectBoxInputClick = useCallback(
    (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
      handleDropdown()
      onClick?.(e)
    },
    [handleDropdown, onClick]
  )

  return (
    <div>
      {label && (
        <TextInputLabel isFocused={open} isDisabled={disabled}>
          {label}
        </TextInputLabel>
      )}
      <div
        {...props}
        role="presentation"
        className="relative w-full"
        onClick={!disabled ? onSelectBoxInputClick : undefined}
      >
        <div
          className={cn(
            'w-full cursor-pointer rounded-xl py-4 pl-4 pr-3 outline-none text-body-1 placeholder:text-gray-500 text-start dark:bg-gray-900 dark:border-gray-500 dark:border',
            valueText ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500',
            {
              'bg-white': color === 'white',
              'bg-background': color === 'background',
            },
            {
              'bg-gray-50 text-gray-300': disabled,
            },
            {
              '!border-extended-error !border-[2px]': !!errorMessage,
            },
            className
          )}
        >
          {valueText || placeholder}
        </div>
        <div className="absolute right-3 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center">
          <Icon
            name="caret_down"
            className={cn('size-6', disabled ? 'fill-gray-300' : 'fill-gray-900 dark:fill-white')}
          />
        </div>
      </div>
      {errorMessage && <p className="text-extended-error text-body-2 mt-2">{errorMessage}</p>}
    </div>
  )
}

export default SelectBoxInput
